// djin-cloud/web/src/components/site-detail/PersistentKPIBar.tsx
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Settings } from 'lucide-react';

interface SiteKPI {
  label: string;
  value: number | null;
  unit: string;
  color?: string;
  tooltip?: string;
}

interface PersistentKPIBarProps {
  siteName: string;
  siteLocation?: string;
  siteGroup?: string;
  status: 'online' | 'offline' | 'degraded' | 'fault';
  kpis: Record<string, number | null>;
  /**
   * When provided, renders an admin-only gear icon next to the status pill
   * linking to the site settings page. Parent decides role eligibility.
   */
  adminSettingsHref?: string;
}

const STATUS_CONFIG = {
  online: { label: 'ONLINE', dotClass: 'bg-ems-green animate-ems-pulse', textClass: 'text-ems-green', bgClass: 'bg-ems-green/20' },
  offline: { label: 'OFFLINE', dotClass: 'bg-ems-text-muted', textClass: 'text-ems-text-muted', bgClass: 'bg-ems-text-muted/20' },
  degraded: { label: 'WARNING', dotClass: 'bg-ems-amber animate-ems-pulse', textClass: 'text-ems-amber', bgClass: 'bg-ems-amber/20' },
  fault: { label: 'FAULT', dotClass: 'bg-ems-red animate-ems-pulse', textClass: 'text-ems-red', bgClass: 'bg-ems-red/20' },
};

function formatValue(val: number | null): string {
  if (val === null || val === undefined) return '--';
  if (Math.abs(val) >= 1000) return val.toLocaleString('en-US', { maximumFractionDigits: 0 });
  return val.toLocaleString('en-US', { maximumFractionDigits: 1 });
}

export function PersistentKPIBar({ siteName, siteLocation, siteGroup, status, kpis, adminSettingsHref }: PersistentKPIBarProps) {
  const statusCfg = STATUS_CONFIG[status] || STATUS_CONFIG.offline;

  // 5-pill reliability-focused layout per djin-cloud#269 (Hibernia QA feedback).
  // Pills not shown here (REACTIVE / APPARENT / GEN CAPACITY total / FUEL) remain
  // available in the in-tab LIVE POWER SUMMARY card and Historian.
  const kpiCards: SiteKPI[] = useMemo(() => [
    { label: 'ACTIVE POWER', value: kpis.total_load_kw ?? null, unit: 'kW', color: 'text-ems-green',
      tooltip: 'Current active power produced/consumed at the site.' },
    { label: 'ONLINE GEN CAPACITY', value: kpis.capacity_online_kw ?? null, unit: 'kW', color: 'text-ems-blue',
      tooltip: 'Online Generator Capacity — sum of nameplate of generators currently connected to the bus.' },
    { label: 'GEN UTILIZATION', value: kpis.site_utilization_pct ?? null, unit: '%', color: 'text-ems-amber',
      tooltip: 'Generator Utilization — current generation / online capacity. Generator-only by design.' },
    { label: 'BESS AVAILABLE', value: kpis.bess_available_kw ?? null, unit: 'kW', color: 'text-ems-cyan',
      tooltip: 'BESS Available Power — sum of rated discharge of online BESS units (reliability asset).' },
    { label: 'BESS ENERGY', value: kpis.bess_available_kwh ?? null, unit: 'kWh', color: 'text-ems-cyan',
      tooltip: 'BESS Available Energy — currently reservable energy across online BESS units.' },
  ], [kpis]);

  // "Armed" = at least one BESS online AND has either reservable power or energy.
  // Treats BESS as a reliability asset (djin-cloud#269): visible only when the
  // BESS can answer a call. kw-only or kwh-only path each satisfies; both null
  // means the operator should not assume BESS will respond.
  const bessOnlineCount = kpis.bess_online_count ?? null;
  const bessAvailableKw = kpis.bess_available_kw ?? null;
  const bessAvailableKwh = kpis.bess_available_kwh ?? null;
  const bessArmed =
    bessOnlineCount !== null &&
    bessOnlineCount > 0 &&
    ((bessAvailableKw !== null && bessAvailableKw > 0) ||
      (bessAvailableKwh !== null && bessAvailableKwh > 0));

  return (
    <div className="flex items-center justify-between px-6 py-4 bg-ems-bg border-b border-ems-border">
      {/* Left: Site identity */}
      <div className="flex items-center gap-3">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-bold text-ems-text-primary">{siteName}</h1>
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${statusCfg.bgClass}`}>
              <span className={`w-2 h-2 rounded-full ${statusCfg.dotClass}`} />
              <span className={statusCfg.textClass}>{statusCfg.label}</span>
            </span>
            {bessArmed && (
              <span
                title="BESS Armed — at least one BESS unit is online and reservable as a reliability asset."
                aria-label="BESS Armed"
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-ems-cyan/20 border border-ems-cyan/30"
              >
                <span className="w-2 h-2 rounded-full bg-ems-cyan animate-ems-pulse" />
                <span className="text-ems-cyan">ARMED</span>
              </span>
            )}
            {adminSettingsHref && (
              <Link
                to={adminSettingsHref}
                title="Edit site settings"
                aria-label="Edit site settings"
                className="p-1.5 rounded-lg text-ems-text-muted hover:text-ems-green hover:bg-ems-bg-card-hover transition-colors"
              >
                <Settings className="w-4 h-4" />
              </Link>
            )}
          </div>
          {(siteLocation || siteGroup) && (
            <p className="text-sm text-ems-text-muted mt-0.5">
              {siteLocation}{siteLocation && siteGroup ? ' · ' : ''}{siteGroup ? `Group: ${siteGroup}` : ''}
            </p>
          )}
        </div>
      </div>

      {/* Right: KPI cards */}
      <div className="flex items-center gap-4 overflow-x-auto">
        {kpiCards.map((kpi) => (
          <div key={kpi.label} className="text-right min-w-[100px]" title={kpi.tooltip}>
            <div className="text-[10px] uppercase tracking-wider text-ems-text-muted font-medium">{kpi.label}</div>
            <div className="flex items-baseline justify-end gap-1 mt-0.5">
              <span className={`text-lg font-bold font-mono ${kpi.color || 'text-ems-text-primary'}`}>
                {formatValue(kpi.value)}
              </span>
              <span className="text-xs text-ems-text-muted">{kpi.unit}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
